import { useCallback, useEffect, useRef, useState } from 'react';
import { type AuditType } from '@amzn/gbsa-helios-typescript-client';
import { useCreateAuditMutation } from '../api';
import { useCurrentUser } from '../../../shared/auth';
import { toMidnightUtc, toEndOfDayUtc } from '../../../shared/domain';

// ─── Types ───────────────────────────────────────────────────────────

/** Form values consumed by the hook to build the API payload. */
export interface CreateAuditFormData {
  readonly name: string;
  readonly location: string;
  readonly divisionName: string;
  readonly templateId: string;
  readonly auditType: AuditType;
  readonly startDate: string; // YYYY-MM-DD
  readonly expiryDate: string; // YYYY-MM-DD
  readonly assignees: string[];
}

export interface UseCreateAuditResult {
  /** Save handler — creates the audit from validated form data. */
  readonly save: (formData: CreateAuditFormData) => void;
  /** Whether the create mutation is in flight. */
  readonly isSaving: boolean;
  /** Raw error message from the last failed mutation, or null. */
  readonly error: string | null;
  /** Clear the current error — call on retry or dismiss. */
  readonly clearError: () => void;
  /** Whether the last save succeeded — flashes true for ~3s. */
  readonly showSuccess: boolean;
}

const SUCCESS_FLASH_MS = 3000;

// ─── Hook ────────────────────────────────────────────────────────────

/**
 * Orchestration hook for creating a new audit.
 *
 * Mirrors the `useCreateTask` pattern: wraps the raw mutation,
 * maps form data → CreateAuditCommandInput, and exposes
 * `{ save, isSaving, error, clearError, showSuccess }` with a
 * transient success flash that the consuming sheet uses to auto-close.
 *
 * The backend creates audits asynchronously (~1 min) so the success
 * message communicates "creation in progress" semantics — the audit
 * won't appear in lists immediately.
 */
export function useCreateAudit(): UseCreateAuditResult {
  const { alias, businessUnit } = useCurrentUser();
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const successTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [createMutation, createState] = useCreateAuditMutation();
  const isSaving = createState.isPending;

  // Cleanup flash timer on unmount.
  useEffect(
    () => () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    },
    [],
  );

  const handleSuccess = useCallback(() => {
    setError(null);
    setShowSuccess(true);
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
    successTimerRef.current = setTimeout(() => {
      setShowSuccess(false);
      successTimerRef.current = null;
    }, SUCCESS_FLASH_MS);
    // List invalidation is handled by useCreateAuditMutation's onSuccess.
  }, []);

  const handleError = useCallback((err: Error) => {
    setError(err.message);
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const save = useCallback(
    (formData: CreateAuditFormData) => {
      if (!businessUnit) return;

      const startDateObj = toMidnightUtc(formData.startDate);

      void createMutation(
        {
          businessUnitId: businessUnit,
          templateId: formData.templateId,
          location: formData.location,
          scheduledDate: startDateObj,
          auditType: formData.auditType,
          name: formData.name.trim(),
          divisionName: formData.divisionName,
          assignees: formData.assignees,
          createdBy: alias,
          startDate: startDateObj,
          expirationDate: toEndOfDayUtc(formData.expiryDate),
        },
        {
          onSuccess: handleSuccess,
          onError: handleError,
        },
      );
    },
    [businessUnit, alias, createMutation, handleSuccess, handleError],
  );

  return { save, isSaving, error, clearError, showSuccess };
}
